import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ChevronRight, 
  Phone, 
  ArrowRight,
  Shield,
  ShieldCheck,
  Building2,
  Users,
  Scale,
  TrendingUp,
  Briefcase,
  Check,
  Calendar,
  Menu,
  X,
  ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEOHead, breadcrumbSchema } from "@/components/SEOHead";

const NBR_LOGO = "https://nationalbusinessregister.co.uk/wp-content/uploads/2024/11/logo.png";
const BOOKINGS_URL = "https://bookings.cloud.microsoft/book/NationalBusinessRegisterGroupLimitedNBR@nbrg.co.uk/s/mbmhEOVCOEG72cEdq6hN4Q2?ismsaljsauthenabled=true";

const TARGET_AUDIENCES = [
  {
    icon: Building2,
    title: "Trade Mark Owners",
    description: "You have registered trade marks with the UK IPO and want to protect them from infringement. Whether you hold one mark or many, proactive monitoring ensures you are alerted to potentially conflicting applications before the opposition deadline passes.",
    benefits: [
      "Automatic alerts when similar marks are filed",
      "Pattern-based analysis of visual, phonetic and conceptual similarities",
      "Opposition deadline tracking so you never miss the 2-month window"
    ]
  },
  {
    icon: Briefcase,
    title: "SMEs with a Portfolio of Trade Marks",
    description: "Small and medium-sized enterprises with multiple registered trade marks benefit significantly from portfolio-wide monitoring. Our volume-based pricing makes it cost-effective to protect every mark in your portfolio — not just the ones you think are at risk.",
    benefits: [
      "Portfolio-wide discounts from 10% to 30%",
      "Single dashboard for all your registered marks",
      "Monthly health check reports covering your entire portfolio"
    ]
  },
  {
    icon: TrendingUp,
    title: "Growing Businesses",
    description: "If your business is expanding and your brand footprint is growing, trade mark monitoring becomes essential. New competitors, copycats, and even inadvertent conflicts can arise as markets grow. We help you stay ahead of these threats.",
    benefits: [
      "Scale your monitoring as your portfolio grows",
      "Better per-mark pricing the more you monitor",
      "Early detection protects your expanding brand equity"
    ]
  },
  {
    icon: Scale,
    title: "In-House Legal & IP Teams",
    description: "For in-house legal professionals and IP managers, our platform streamlines the trade mark watching process. Instead of manual registry checks, our automated scanning and reporting frees up time for higher-value work.",
    benefits: [
      "Automated weekly scanning replaces manual checks",
      "Branded PDF reports for internal stakeholders",
      "Detailed similarity analysis for informed decision-making"
    ]
  }
];

export const AboutPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="About You - Is This Service Right For You?"
        description="NBR Trade Mark Monitoring is built for UK trade mark owners, SMEs with portfolios of registered marks, growing businesses, and in-house legal teams who want proactive protection against infringement."
        path="/about"
        schema={breadcrumbSchema([
          { name: "Home", url: "/landing" },
          { name: "About You", url: "/about" }
        ])}
      />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/landing" className="flex items-center gap-3">
              <img src={NBR_LOGO} alt="National Business Register" className="h-8 sm:h-10 w-auto" />
              <div className="hidden sm:block">
                <span className="font-heading font-black text-lg text-slate-900">NBR</span>
                <span className="text-xs text-teal-600 tracking-widest uppercase ml-1">Trade Mark Monitoring</span>
              </div>
            </Link>
            
            <div className="hidden md:flex items-center gap-6">
              <Link to="/landing" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">Home</Link>
              <Link to="/landing#methodology" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">Features</Link>
              <Link to="/landing#how-it-works" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">How It Works</Link>
              <Link to="/landing#pricing" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">Pricing</Link>
              <Link to="/about" className="text-sm text-teal-600 font-medium">About You</Link>
              <Link to="/faq" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">FAQs</Link>
              <Link to="/blog" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">Blog</Link>
              <a href="tel:08000699090" className="text-sm text-slate-600 hover:text-teal-600 flex items-center gap-1 transition-colors">
                <Phone className="w-4 h-4" />
                0800 069 9090
              </a>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3">
              <Link to="/register-trademarks">
                <Button className="bg-teal-600 hover:bg-teal-700 text-white text-sm px-3 sm:px-4">
                  Get Started
                </Button>
              </Link>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-slate-600 hover:text-teal-600 transition-colors"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
        
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 shadow-lg px-4 py-4 space-y-3">
            <Link to="/landing" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-slate-600 hover:text-teal-600 font-medium">Home</Link>
            <Link to="/landing#methodology" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-slate-600 hover:text-teal-600 font-medium">Features</Link>
            <Link to="/landing#how-it-works" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-slate-600 hover:text-teal-600 font-medium">How It Works</Link>
            <Link to="/landing#pricing" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-slate-600 hover:text-teal-600 font-medium">Pricing</Link>
            <Link to="/faq" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-slate-600 hover:text-teal-600 font-medium">FAQs</Link>
            <Link to="/blog" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-slate-600 hover:text-teal-600 font-medium">Blog</Link>
            <a href={BOOKINGS_URL} target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-slate-600 hover:text-teal-600 font-medium">Book a Demo</a>
            <a href="tel:08000699090" className="flex items-center gap-2 py-2 text-teal-600 font-medium">
              <Phone className="w-4 h-4" /> 0800 069 9090
            </a>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 text-sm text-slate-500 mb-4">
            <Link to="/landing" className="hover:text-teal-600">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-900">About You</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-black text-slate-900 mb-4">
            Is This Service Right For You?
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            NBR Trade Mark Monitoring is built for UK trade mark owners and businesses with portfolios 
            of registered marks who want proactive protection against infringement.
          </p>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="px-4 sm:px-6 lg:px-8 pb-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
            {TARGET_AUDIENCES.map((audience, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-xl border border-slate-200 p-6 sm:p-8 hover:shadow-lg hover:border-teal-200 transition-all"
              >
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <audience.icon className="w-6 h-6 text-teal-600" />
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-3">{audience.title}</h2>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">{audience.description}</p>
                <ul className="space-y-2">
                  {audience.benefits.map((benefit, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-teal-500 flex-shrink-0 mt-0.5" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why NBR */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-heading font-black text-slate-900 mb-4">
              Why Choose NBR Trade Mark Monitoring?
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Backed by National Business Register's 40+ years of IP expertise, our monitoring 
              platform combines proven industry knowledge with modern technology.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-6">
            {[
              {
                icon: Shield,
                title: "40+ Years of IP Expertise",
                description: "National Business Register has protected thousands of businesses since 1984."
              },
              {
                icon: ShieldCheck,
                title: "Pattern-Based Detection",
                description: "Advanced analysis of visual, phonetic, and conceptual similarities across trade marks."
              },
              {
                icon: Users,
                title: "Specialist Support",
                description: "Our trade mark specialists are available for consultations and can manage opposition proceedings."
              }
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-xl p-6 border border-slate-200 text-center"
              >
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-6 h-6 text-teal-600" />
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-10">
            <a 
              href="https://nationalbusinessregister.co.uk/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-teal-600 hover:text-teal-700 font-medium transition-colors"
            >
              Learn more about National Business Register
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Not Right For You */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl sm:text-2xl font-heading font-bold text-slate-900 mb-4 text-center">
            When This Service May Not Be What You Need
          </h2>
          <div className="bg-slate-50 rounded-xl p-6 sm:p-8 border border-slate-200">
            <ul className="space-y-3 text-slate-600 text-sm">
              <li className="flex items-start gap-3">
                <span className="text-slate-400 font-bold">—</span>
                <span>If you do not yet have a registered UK trade mark, we recommend <a href="https://nationalbusinessregister.co.uk/registering-for-a-trade-mark/" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">registering your trade mark first</a>.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-slate-400 font-bold">—</span>
                <span>If you need international monitoring only (outside the UK), we are working on EUIPO and WIPO coverage — <Link to="/faq" className="text-teal-600 hover:underline">see our FAQs</Link> for details.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-slate-400 font-bold">—</span>
                <span>If you are looking for legal advice on a current dispute, please <a href="https://nationalbusinessregister.co.uk/contact/" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">contact NBR directly</a>.</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-teal-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-heading font-black text-white mb-4">
            Ready to Protect Your Brand?
          </h2>
          <p className="text-teal-100 mb-8 max-w-2xl mx-auto">
            Get started with NBR Trade Mark Monitoring or book a free demonstration with our team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register-trademarks">
              <Button className="bg-white text-teal-700 hover:bg-slate-100 px-8 py-6">
                Get Started
              </Button>
            </Link>
            <Button 
              onClick={() => window.open(BOOKINGS_URL, '_blank')}
              variant="outline" 
              className="border-white text-white hover:bg-white/10 px-8 py-6"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Book Free Consultation
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <img src={NBR_LOGO} alt="National Business Register" className="h-14 w-auto brightness-0 invert" />
              <div>
                <span className="font-heading font-black text-lg text-white">NBR</span>
                <span className="text-xs text-teal-400 tracking-widest uppercase ml-1">Trade Mark Monitoring</span>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-400">
              <a href="https://nationalbusinessregister.co.uk/" target="_blank" rel="noopener noreferrer" className="hover:text-teal-400 transition-colors flex items-center gap-1">
                nationalbusinessregister.co.uk <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <p className="text-slate-400 text-sm text-center">
              &copy; {new Date().getFullYear()} National Business Register Group Ltd.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
